import gql from 'graphql-tag';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import Field from '../../../../../modules/react/components/stateful/Field';
import SelectInput from '../../../../../modules/react/components/stateful/SelectInput';
import useMutation from '../../../../../modules/react/hooks/useMutation';
import {
  getBackgroundType,
  getImages,
  getInitialImages,
  getUsedMedia,
  isMediaGalleryOpen,
  setBackgroundType,
  setInitialImages,
  setUsedMedium,
  toggleMediaGallery,
  uploadImages,
} from '../../../store/modules/mediaGallery';
import ColorPickerInput from '../../form/inputs/ColorPicker/ColorPicker';
import RadioButtonsInput from '../../form/inputs/Radio/Radio';
import ImageSettingEditor from './ImageSettingEditor';
import SlideshowSettingEditor from './SlideshowSettingEditor';
import EmbedVideoSettings from './embedVideoSettings';
import classes from './section.scss';

const UPLOAD_MEDIUM = gql`
  mutation MediumUpload($file: Upload!) {
    mediumUpload(file: $file) {
      medium {
        id
        trash
        companyId
        filename
        url
        formattedUpdatedAt
        defaultMetadata {
          title
          altText
        }
      }
      userErrors {
        code
        message
      }
    }
  }
`;

export const OPTION_COLOR = 'color';
export const OPTION_IMAGE = 'image';
export const OPTION_VIDEO = 'video';
export const OPTION_NONE = 'none';

const radioOptions = {
  [OPTION_NONE]: 'None',
  [OPTION_COLOR]: 'Color',
  [OPTION_IMAGE]: 'Image',
  [OPTION_VIDEO]: 'Video',
};

const PATTERN_DOORS = 'doors';
const COLOR_TRANSPARENT = 'transparent';

export const OPTION_PATTERN = 'doors';

const availablePatterns = [
  { value: 'None', label: 'None' },
  { value: PATTERN_DOORS, label: 'Storage Doors' },
];

const additionalColors = [
  { value: COLOR_TRANSPARENT, label: 'None' },
  { value: '#f8f8f8', label: 'Light Grey' },
];

const formatPattern = ({ value } = {}) => value || 'None';
const formatType = value => value || OPTION_NONE;

const BackgroundSettings = (props) => {
  const { sectionStyle, isMediaGalleryVisible,
    mediaGalleryToggle, images, setImages, usedMedia, setUsedMedia, initialImages, setExistingImages,
    backgroundType, setBackgroundStyleType } = props;

  const sectionBackgroundType = backgroundType || sectionStyle?.backgroundStyle || OPTION_NONE;

  useEffect(() => {
    if (initialImages.length === 0 && images.length === 0) {
      setExistingImages(sectionStyle?.images || []);
    }
  }, []);

  const changeHandler = (e) => {
    setBackgroundStyleType(e.target.value);
  };

  const [
    uploadImage, { loading, data: uploadData },
  ] = useMutation(UPLOAD_MEDIUM, { refetchQueries: ['GetAllMedia'] });

  const onUpload = ({ target: { files: [file] } }) => {
    if (!file) { return; }
    uploadImage({ variables: { file } });
  };


  return (
    <div>
      <Field
        label="Background Type"
        component={RadioButtonsInput}
        name="sectionStyle.backgroundStyle"
        options={radioOptions}
        format={formatType}
        onChange={changeHandler}
        labelClassName={classes.fieldLabelClass}
        radioOptionsClass={classes.radioButtonOptions}
      />
      {sectionBackgroundType === 'color' && (
        <div>
          <h4 className={classes.menuHeading}>Color Settings</h4>
          <Field
            label="Background Color"
            labelClassName={classes.fieldLabelClass}
            component={ColorPickerInput}
            name="sectionStyle.backgroundColor"
            additionalColors={additionalColors}
          />
          <div>
            <Field
              label="Pattern"
              labelClassName={classes.fieldLabelClass}
              component={SelectInput}
              name="sectionStyle.backgroundPattern"
              instanceId="pattern-selector"
              clearable={false}
              searchable={false}
              format={formatPattern}
              options={availablePatterns}
              className={classes.patternSelect}
              allowCustom
            />
          </div>
        </div>
      )}

      {sectionBackgroundType === 'image' && (
        <div className={classes.uploadInput}>
          <h4>Image Settings</h4>
          <p style={{ marginTop: '16px', marginBottom: '-8px' }}>
            {
              [
                'Upload or select an image to add it to the background of your section.',
                'You may add multiple images to create a slideshow.',
                'Editing Alt or Title Text will update it everywhere the image is used.',
              ].join(' ')
            }
          </p>
          <Field
            component={ImageSettingEditor}
            name="sectionStyle.images"
            loading={loading}
            onUpload={onUpload}
            data={uploadData}
            showGallery={isMediaGalleryVisible}
            setShowGallery={mediaGalleryToggle}
            usedMedia={usedMedia}
            setUsedMedia={setUsedMedia}
            images={images}
            setImages={setImages}
          />
        </div>
      )}
      {sectionBackgroundType === 'image' && images.length > 1 && (
        <div className={classes.uploadInput}>
          <h4 style={{ marginTop: '16px', marginBottom: '16px' }}>Slideshow Settings</h4>
          <SlideshowSettingEditor slideshowSetting={sectionStyle?.slideshowSetting || {}} />
        </div>
      )}
      {sectionBackgroundType === 'video' && (
        <EmbedVideoSettings {...props} />
      )}
    </div>
  );
};

export default connect(
  state => ({
    isMediaGalleryVisible: isMediaGalleryOpen(state),
    images: getImages(state),
    usedMedia: getUsedMedia(state),
    initialImages: getInitialImages(state),
    backgroundType: getBackgroundType(state),
  }),
  {
    mediaGalleryToggle: toggleMediaGallery,
    setImages: uploadImages,
    setUsedMedia: setUsedMedium,
    setExistingImages: setInitialImages,
    setBackgroundStyleType: setBackgroundType,
  },
)(BackgroundSettings);
